// @flow

import debugLib from 'debug';
import R from 'ramda';

import {
  ORG_INITIALIZED,
  FETCH_CURRENT_ORG,
  FETCH_APPS_AND_ORGS,
  SAVE_ORG_ATTRIBUTION_WINDOWS,
  SAVE_ORG_SETTINGS
} from '../actions/OrgActions';

import {
  reducerWrapper,
  findAgencyId,
  determineLoadingStatus
} from '../support/utils';
import { LOADING_STATUS } from '../../main/support/constants';

import {
  ORG_VIEW,
  AGENCY_VIEW,
  ORG_VIEW_ID_FIELD,
  ATTRIBUTION_SETTINGS_KEY
} from '../support/viewConstants';

import type {
  OrgReducer as OrgReducerType,
  OrgActionPayload
} from '../types/orgTypes';

const { flipper, view } = window.App.context;
const userOnOrgView = view && view.current_view === ORG_VIEW;
const userOnAgencyView = view && view.current_view === AGENCY_VIEW;
const organization_id =
  userOnOrgView || userOnAgencyView ? view[ORG_VIEW_ID_FIELD] : null;

export const INIT_STATE: OrgReducerType = {
  error: null,
  loadingStatus: LOADING_STATUS.INIT,
  appsAndOrgsLoadingStatus: LOADING_STATUS.INIT,
  currentOrgLoadingStatus: LOADING_STATUS.INIT,
  currentOrg: null,
  organization_id,
  appsAndOrgs: [],
  userOnOrgView: flipper && flipper.organization && userOnOrgView,
  userOnAgencyView: flipper && flipper.organization && userOnAgencyView,
  // For agency users, agency_id will be the organization_id of their parent agency
  agency_id: null,
  initializationComplete: false
};

const debug = debugLib('OrgReducer');

const loadingStates = ['appsAndOrgsLoadingStatus', 'currentOrgLoadingStatus'];

function Handler(state: OrgReducerType = INIT_STATE, action: OrgActionPayload) {
  switch (action.type) {
    case ORG_INITIALIZED:
      return {
        ...state,
        initializationComplete: true
      };
    case FETCH_APPS_AND_ORGS.response:
      const { appsAndOrgs = [] } = action.data;
      const fetchAppOrgState = {
        ...state,
        appsAndOrgs,
        appsAndOrgsLoadingStatus: LOADING_STATUS.LOADED,
        agency_id: findAgencyId(appsAndOrgs)
      };
      fetchAppOrgState.loadingStatus = determineLoadingStatus(
        fetchAppOrgState,
        loadingStates
      );
      return fetchAppOrgState;
    case FETCH_CURRENT_ORG.response:
      const { org } = action.data;
      const fetchCurrentOrgState = {
        ...state,
        currentOrg: org,
        currentOrgLoadingStatus: LOADING_STATUS.LOADED
      };

      fetchCurrentOrgState.loadingStatus = determineLoadingStatus(
        fetchCurrentOrgState,
        loadingStates
      );
      return fetchCurrentOrgState;

    case SAVE_ORG_ATTRIBUTION_WINDOWS.response:
      debug(action);
      const windows = action.data && action.data.windows;
      return {
        ...state,
        currentOrg: {
          ...state.currentOrg,
          [ATTRIBUTION_SETTINGS_KEY]: windows
        },
        error: null,
        loadingStatus: LOADING_STATUS.LOADED
      };

    case SAVE_ORG_SETTINGS.response:
      debug(action);
      if (!action.data) {
        return state;
      }

      // reload the window if timezone has changed
      if (action.data.timezone !== R.path(['currentOrg', 'timezone'], state)) {
        window.location.reload();
      }
      return {
        ...state,
        loading: false,
        error: null,
        currentOrg: action.data,
        loadingStatus: LOADING_STATUS.LOADED
      };

    default:
      return state;
  }
}

const OrgReducer = reducerWrapper(INIT_STATE, Handler, {
  request: [
    FETCH_CURRENT_ORG,
    FETCH_APPS_AND_ORGS,
    SAVE_ORG_ATTRIBUTION_WINDOWS
  ],
  error: [FETCH_CURRENT_ORG, FETCH_APPS_AND_ORGS, SAVE_ORG_ATTRIBUTION_WINDOWS]
});

export default OrgReducer;
